import { useState, useEffect, useCallback } from 'react';
import { clubService, DiveClub, CreateClubRequest, ClubJoinModeValue } from '../services/clubService';

export function useClubs(userId: string | null) {
  const [clubs, setClubs] = useState<DiveClub[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    setError(null);
    try {
      const c = await clubService.getMyClubs();
      setClubs(c);
    } catch {
      setError('Failed to load clubs');
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => { refresh(); }, [refresh]);

  const createClub = useCallback(async (name: string, description?: string, joinMode?: ClubJoinModeValue) => {
    const request: CreateClubRequest = { name, description, joinMode };
    const club = await clubService.createClub(request);
    await refresh();
    return club;
  }, [refresh]);

  const joinByCode = useCallback(async (shareCode: string) => {
    const club = await clubService.joinByCode(shareCode);
    await refresh();
    return club;
  }, [refresh]);

  const requestJoin = useCallback(async (clubId: string) => {
    const membership = await clubService.requestJoin(clubId);
    await refresh();
    return membership;
  }, [refresh]);

  const leaveClub = useCallback(async (clubId: string) => {
    if (!userId) return;
    await clubService.removeMember(clubId, userId);
    await refresh();
  }, [userId, refresh]);

  return { clubs, loading, error, refresh, createClub, joinByCode, requestJoin, leaveClub };
}
